import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Search, Sparkles, Send, ShieldAlert, UserPlus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { insApi, errMsg, KIND_LABEL, EXAM_LABEL } from "../../lib/insApi";
import { BorderEmployeeForm } from "./BorderEmployeeForm";
import { personMatch } from "../../lib/searchText";

export const InsureNowDialog = ({ open, onOpenChange, kind, channel = "market", onSent }) => {
  const [items, setItems] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [q, setQ] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [policies, setPolicies] = useState([]);
  const [target, setTarget] = useState("");
  const [addBorder, setAddBorder] = useState(false);
  const [exams, setExams] = useState(["iqama"]);
  const medical = kind === "medical";
  useEffect(() => {
    if (!open) return;
    setItems(null); setSel(new Set()); setQ(""); setNotes(""); setAddBorder(false); setExams(["iqama"]);
    insApi.candidates(kind).then((r) => { setItems(r.items); setPolicies(r.policies || []); setTarget(r.policies?.[0]?.id || ""); })
      .catch((e) => { toast.error(errMsg(e)); setItems([]); });
  }, [open, kind]);
  const onBorder = (emp) => {
    setItems((l) => [{ ...emp, suggested: true, reasons: ["جديد برقم الحدود"], on_policy: "" }, ...(l || [])]);
    setSel((s) => new Set([...s, emp.id])); setAddBorder(false);
  };
  const suggested = useMemo(() => (items || []).filter((x) => x.suggested), [items]);
  const shown = useMemo(() => (items || []).filter((x) => personMatch(x, q)), [items, q]);
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const send = async () => {
    setBusy(true);
    try {
      await insApi.createRequest({ kind, employee_ids: [...sel], notes, add_to_policy: target, channel, exam_types: medical ? exams : [] });
      toast.success(target ? "تم إرسال طلب الإضافة لمزود بوليصتك الحالية" : "تم إرسال الطلب — ستصلك عروض الأسعار خلال ساعتين");
      onOpenChange(false); onSent?.();
    } catch (e) { toast.error(errMsg(e, "تعذّر الإرسال")); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="max-w-2xl font-body" data-testid="insure-now-dialog">
        <DialogHeader><DialogTitle className="font-heading text-right">{channel === "rabet" ? "تأمين ربط" : "طلب عروض أسعار"} — {KIND_LABEL[kind]}</DialogTitle></DialogHeader>
        {items === null ? <div className="py-12 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" /></div> : (
          <div className="space-y-3">
            {medical && (
              <div className="rounded-xl border border-rose-200 bg-rose-50/60 p-3" data-testid="insure-exam-types">
                <p className="mb-2 text-sm font-semibold text-rose-900">نوع الفحص المطلوب</p>
                <div className="flex flex-wrap gap-4 text-sm">{Object.entries(EXAM_LABEL).map(([k, l]) => (
                  <label key={k} className="flex items-center gap-1.5"><Checkbox checked={exams.includes(k)} data-testid={`insure-exam-${k}`}
                    onCheckedChange={() => setExams((e) => (e.includes(k) ? e.filter((x) => x !== k) : [...e, k]))} />{l}</label>
                ))}</div>
              </div>
            )}
            {policies.length > 0 && (
              <div className="space-y-2 rounded-xl border border-sky-200 bg-sky-50 p-2.5 text-sm" data-testid="insure-policy-alert">
                <p className="flex items-center gap-1.5 font-semibold text-sky-900"><ShieldAlert className="h-4 w-4" /> يوجد لديك بوليصة سارية — سيتم إضافة المحددين لنفس البوليصة</p>
                <select data-testid="insure-target-policy" value={target} onChange={(e) => setTarget(e.target.value)} className="h-9 w-full rounded-md border border-sky-200 bg-white px-2 text-sm">
                  {policies.map((p) => <option key={p.id} value={p.id}>إضافة إلى بوليصة {p.policy_number} — {p.provider_name} (حتى {p.expiry_date})</option>)}
                  <option value="">لا — اطلب عروض أسعار جديدة من كل الشركات</option>
                </select>
              </div>
            )}
            {suggested.length > 0 && (
              <div className="flex items-center justify-between gap-2 rounded-xl bg-amber-50 border border-amber-200 p-2.5 text-sm">
                <span className="flex items-center gap-1.5 text-amber-800"><Sparkles className="h-4 w-4" /> نقترح {suggested.length} موظف ({medical ? "إقامة منتهية / قريبة الانتهاء" : "إقامة أو تأمين منتهٍ / قريب الانتهاء"})</span>
                <Button size="sm" variant="outline" data-testid="insure-select-suggested" onClick={() => setSel(new Set(suggested.map((x) => x.id)))}>تحديد المقترحين</Button>
              </div>
            )}
            <div className="flex gap-2">
              <div className="relative flex-1"><Search className="absolute right-2.5 top-2.5 h-4 w-4 text-slate-400" />
                <Input data-testid="insure-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث بالاسم أو رقم الإقامة" className="pr-8" /></div>
              <Button variant="outline" size="sm" data-testid="insure-add-border" onClick={() => setAddBorder((v) => !v)} className="gap-1"><UserPlus className="h-4 w-4" /> برقم الحدود</Button>
              <Button variant="ghost" size="sm" data-testid="insure-select-all" onClick={() => setSel((s) => { const all = shown.length && shown.every((x) => s.has(x.id)); const n = new Set(s); shown.forEach((x) => (all ? n.delete(x.id) : n.add(x.id))); return n; })}>{shown.length && shown.every((x) => sel.has(x.id)) ? "إلغاء الكل" : "تحديد الكل"}</Button>
            </div>
            {addBorder && <BorderEmployeeForm kind={kind} onDone={onBorder} />}
            <div className="max-h-72 overflow-y-auto rounded-xl border border-slate-200 divide-y divide-slate-100">
              {shown.length === 0 && <p className="py-8 text-center text-sm text-slate-400">لا يوجد موظفون</p>}
              {shown.map((x) => (
                <label key={x.id} className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-slate-50" data-testid={`insure-emp-${x.id}`}>
                  <Checkbox checked={sel.has(x.id)} onCheckedChange={() => toggle(x.id)} />
                  {kind === "individuals" || medical || !x.vehicle_number ? (
                    <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold">{x.name}</p>
                      <p className="text-xs text-slate-400" dir="ltr">{x.iqama_number}</p></div>
                  ) : (
                    <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold" dir="ltr">{x.vehicle_number}</p>
                      <p className="truncate text-xs text-slate-400">{x.name}</p></div>
                  )}
                  {x.on_policy && <span className="rounded-full bg-sky-50 px-2 py-0.5 text-[11px] text-sky-700">على بوليصة {x.on_policy}</span>}
                  {x.reasons.map((r) => <span key={r} className="rounded-full bg-rose-50 px-2 py-0.5 text-[11px] text-rose-700">{r}</span>)}
                </label>
              ))}
            </div>
            <Textarea data-testid="insure-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="ملاحظات للمزودين (فئة التغطية المطلوبة، المدينة...) — اختياري" rows={2} />
            {[...sel].some((id) => { const x = items.find((i) => i.id === id); return x && (!x.birth_date || !x.nationality); }) &&
              <p className="rounded-lg bg-amber-50 p-2 text-xs text-amber-800" data-testid="insure-missing-data">تنبيه: بعض الموظفين المحددين بدون تاريخ ميلاد أو جنسية في ملفاتهم — أكملها للحصول على أسعار أدق.</p>}
            <p className="text-xs text-slate-500">يرى المزودون بيانات التسعير فقط (العمر، الجنسية، نوع المركبة). الأسماء وأرقام الإقامة تظهر للمزود اللي تقبل عرضه فقط.</p>
            <Button data-testid="insure-send" disabled={!sel.size || busy || (medical && !exams.length)} onClick={send} className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {target ? "إرسال طلب الإضافة للبوليصة" : "إرسال الطلب لشركات التأمين"} ({sel.size})
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
